/**
 * Whose login page is this?
 *
 * Resolved from window.location.hostname BEFORE React renders and before
 * anybody signs in, because the sign-in screen is the one page where the
 * existing branding path - look up the org from the user's claims - has no user
 * to look anything up from. A customer typing backups.vtgtx.com should see
 * their own name and mark, not ours, at the moment they are deciding whether
 * this is a real system.
 *
 * THE TAB ICON IS NOT PER TENANT. It is the product's, baked into index.html,\n * for the same reason the wordmark stays in the header: a tab says which\n * software this is. Only the TITLE and the logos are resolved here.\n *\n * PUBLIC BY NECESSITY, AND ONLY THIS. `hosts/{hostname}` is world-readable
 * because it must be read with no session. It therefore contains exactly what a
 * login page prints - a title and two logos - and nothing else. See
 * functions/src/hosts.ts for why this is a separate collection rather than a
 * public field on the org.
 *
 * FAILS SILENT, ALWAYS. No record, no network, no permission: the app falls
 * back to the bundled default and renders. Branding is the one thing on this
 * page that must never be able to stop somebody signing in - which is precisely
 * the risk of resolving it before the app starts.
 */

import { doc, getDoc } from 'firebase/firestore';
import { db } from './firebase';

export interface HostBrand {
  title: string | null;
  logoLight: string | null;
  logoDark: string | null;
}

const NONE: HostBrand = { title: null, logoLight: null, logoDark: null };

let resolved: HostBrand = NONE;

/** What the last resolve found. Synchronous, so render paths need no await. */
export function hostBrand(): HostBrand {
  return resolved;
}

/**
 * Look up this hostname and apply what it says.
 *
 * Awaited before the first render, but with a hard timeout: a slow lookup must
 * delay the sign-in form by a beat, never withhold it. Two seconds is longer
 * than a Firestore document read has any business taking and short enough that
 * nobody reaches for the reload button.
 */
export async function resolveHostBrand(timeoutMs = 2000): Promise<HostBrand> {
  const host = window.location.hostname.toLowerCase();

  // Nothing is mapped for the default Firebase hosts or for local development,
  // and asking is a wasted round trip on every load in exactly the places we
  // develop and test.
  if (!host || host === 'localhost' || host.endsWith('.web.app') || host.endsWith('.firebaseapp.com')) {
    return NONE;
  }

  try {
    const race = await Promise.race([
      getDoc(doc(db, 'hosts', host)),
      new Promise<null>((r) => setTimeout(() => r(null), timeoutMs)),
    ]);
    if (!race || !('exists' in race) || !race.exists()) return NONE;

    const d = race.data() as Partial<HostBrand>;
    resolved = {
      title: d.title ?? null,
      logoLight: d.logoLight ?? null,
      logoDark: d.logoDark ?? null,
    };

    if (resolved.title) document.title = resolved.title;
    return resolved;
  } catch {
    // Includes the offline case and a rules change that closed the collection.
    // Neither is a reason to show nobody a login form.
    return NONE;
  }
}
